import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Map as MapIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Item, ItemActions, ItemContent } from '@/components/ui/item';
import { Skeleton } from '@/components/ui/skeleton';
import SitrepCardHeader from 'components/Reports/SitrepCardHeader';
import {
  firstSectionAreaScope,
  useReportAreaScope,
} from 'components/Reports/automated/useReportAreaScope';
import useOpenReportSelection from 'components/Reports/hooks/useOpenReportSelection';
import useSitrepCardContentData from 'components/Reports/hooks/useSitrepCardContentData';
import {
  SitrepCardSurface,
  type GetSitrepsResponse,
} from 'components/Reports/reports.types';
import { useAorText } from 'utils/aorTerminology';

type SitrepCardContentData = ReturnType<typeof useSitrepCardContentData>;

/**
 * Props accepted by {@link SitrepCardShell}.
 *
 * The shell handles the outermost card chrome (`Item`, sticky header, loading
 * state). The card stays open. The content area is rendered by the caller via
 * `children`.
 *
 * Callers provide the already-fetched shell data they are using for the rest of
 * the card, so the shell stays presentation-focused.
 */
type SitrepCardShellProps = React.PropsWithChildren<{
  sitrep: GetSitrepsResponse;
  surface: SitrepCardSurface;
  headerRight?: React.ReactNode;
  /** Inline with status badges (e.g. Edit before Approval). */
  headerTrailing?: React.ReactNode;
  /** Last row of the sticky header, below title, actions, and scope. */
  headerBanner?: React.ReactNode;
  cardData: SitrepCardContentData;
  /** Historical cards start collapsed and show the body only after expand. */
  collapsible?: boolean;
}>;

const SitrepCardShell: React.FC<SitrepCardShellProps> = ({
  sitrep,
  surface,
  headerRight,
  headerTrailing,
  headerBanner,
  cardData,
  collapsible = false,
  children,
}) => {
  const aorText = useAorText();
  const [expanded, setExpanded] = useState(false);
  const showBody = !collapsible || expanded;
  const { isOpenReportMatch } = useOpenReportSelection(
    sitrep.id,
    sitrep.item_id
  );

  const { sectionsLoading, sitrepData } = cardData;
  const canEditArea =
    surface === SitrepCardSurface.Draft && sitrep.approval_status !== 'PENDING';
  const legacyPersistedEntry = useMemo(
    () => firstSectionAreaScope(sitrepData?.sections),
    [sitrepData?.sections]
  );
  const areaScope = useReportAreaScope({
    sitrep: sitrepData || sitrep,
    sitrepId: sitrep.id,
    legacyPersistedEntry,
    persist: canEditArea,
  });

  return (
    <div
      className="reports-card"
      // Reports tour: spotlight only the draft the walkthrough just created.
      data-tour={
        (surface === SitrepCardSurface.Draft ||
          surface === SitrepCardSurface.Review) &&
        isOpenReportMatch
          ? 'reports-draft-card'
          : undefined
      }
    >
      <Item
        variant="outline"
        className="tw:min-w-0 tw:max-w-full tw:flex-col tw:items-stretch tw:gap-0 tw:overflow-hidden tw:border-border tw:bg-card tw:p-0 tw:text-card-foreground tw:transition-colors tw:hover:bg-muted/40"
      >
        <div
          className={
            showBody
              ? 'tw:sticky tw:top-0 tw:z-10 tw:flex tw:flex-col tw:gap-1 tw:rounded-t-md tw:border-b tw:border-border tw:bg-card tw:px-3 tw:py-2.5'
              : 'tw:flex tw:flex-col tw:gap-1 tw:bg-card tw:px-3 tw:py-2.5'
          }
        >
          <div className="tw:flex tw:items-start tw:gap-2">
            {collapsible ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                className="tw:mt-0.5 tw:shrink-0"
                aria-expanded={expanded}
                aria-label={expanded ? 'Collapse report' : 'Expand report'}
                onClick={() => setExpanded((open) => !open)}
              >
                {expanded ? (
                  <ChevronDown className="tw:h-3.5 tw:w-3.5" />
                ) : (
                  <ChevronRight className="tw:h-3.5 tw:w-3.5" />
                )}
              </Button>
            ) : null}
            <ItemContent className="tw:min-w-0">
              <SitrepCardHeader
                sitrep={sitrep}
                surface={surface}
                areaScope={areaScope}
                canEditArea={canEditArea}
                includeScopeBar={false}
              />
            </ItemContent>
            <ItemActions className="tw:shrink-0 tw:self-start">
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                disabled={!areaScope.effectiveScope?.spatialFilter}
                aria-label={
                  sitrep.incident_id
                    ? 'Zoom to incident location'
                    : aorText('Zoom to AOR location')
                }
                title={
                  sitrep.incident_id
                    ? 'Zoom to incident location'
                    : aorText('Zoom to AOR location')
                }
                onClick={(event) => {
                  event.stopPropagation();
                  areaScope.handleZoomToScope();
                }}
              >
                <MapIcon className="tw:h-3.5 tw:w-3.5" />
              </Button>
              {headerRight}
            </ItemActions>
          </div>
          <SitrepCardHeader
            sitrep={sitrep}
            surface={surface}
            areaScope={areaScope}
            canEditArea={canEditArea}
            trailing={headerTrailing}
            includeTitle={false}
          />
          {headerBanner}
        </div>
        {showBody ? (
          <div className="tw:min-w-0 tw:max-w-full tw:overflow-x-clip tw:rounded-b-md">
            <div className="tw:min-w-0 tw:max-w-full tw:px-3 tw:py-3 tw:text-foreground">
              {sectionsLoading ? (
                <div className="tw:space-y-2">
                  <Skeleton className="tw:h-4 tw:w-2/3" />
                  <Skeleton className="tw:h-4 tw:w-full" />
                  <Skeleton className="tw:h-4 tw:w-5/6" />
                </div>
              ) : (
                children
              )}
            </div>
          </div>
        ) : null}
      </Item>
    </div>
  );
};

export default SitrepCardShell;
